interface IPendingRequest<TRequest, TResult> {
  request: TRequest
  resolve: (result: TResult) => void
  reject: (error: unknown) => void
}

export interface ICreateRequestBatcherParams<TRequest, TResult> {
  batchLimit: number
  fetchBatch: (requests: TRequest[]) => Promise<TResult[]>
  keyOf: (request: TRequest) => string
  resultKeyOf: (result: TResult) => string
  fallback: (request: TRequest) => TResult
}

export function createRequestBatcher<TRequest, TResult>(params: ICreateRequestBatcherParams<TRequest, TResult>): (request: TRequest) => Promise<TResult> {
  const { batchLimit, fetchBatch, keyOf, resultKeyOf, fallback } = params
  let pending: IPendingRequest<TRequest, TResult>[] = []
  let flushScheduled = false

  const flush = async (): Promise<void> => {
    const batch = pending
    pending = []

    for (let i = 0; i < batch.length; i += batchLimit) {
      const chunk = batch.slice(i, i + batchLimit)
      try {
        // Duplicate keys are passed through: only fetchBatch knows how to merge same-key
        // requests without dropping information (e.g. a forceFresh flag).
        const results = await fetchBatch(chunk.map((entry) => entry.request))
        const byKey = new Map(results.map((result) => [resultKeyOf(result), result]))
        for (const entry of chunk) {
          entry.resolve(byKey.get(keyOf(entry.request)) ?? fallback(entry.request))
        }
      } catch (error) {
        for (const entry of chunk) {
          entry.reject(error)
        }
      }
    }
  }

  return (request: TRequest) =>
    new Promise<TResult>((resolve, reject) => {
      pending.push({ request, resolve, reject })
      if (!flushScheduled) {
        flushScheduled = true
        queueMicrotask(() => {
          flushScheduled = false
          void flush()
        })
      }
    })
}
